const DEBUG = true;
const BH_APP_API_URL = "https://bh-app-api.bombayhair.ca/api/v1";
const WISHLIST_END_POINT = "/wishlist";
const WISHLIST_API_URL = `${BH_APP_API_URL}${WISHLIST_END_POINT}`;
const GET_WISHLIST_DATA_URL = `${WISHLIST_API_URL}/get-data`;
const TOGGLE_WISHLIST_DATA_URL = `${WISHLIST_API_URL}/toggle`;
const WISHLIST_STATUS_URL = `${WISHLIST_API_URL}/status`;
const BH_APP_KEY = "bh_app_8e98yuweuiwer7uirtew";
let CUSTOMER_ID = null;

// ─── Utilities ────────────────────────────────────────────────────────────────

const showToast = (message) => {
  Toastify({
    text: message,
    duration: 3000,
    close: true,
    gravity: "bottom",
    position: "right",
    style: { background: "#cd0c63" },
    stopOnFocus: true,
    escapeMarkup: false,
  }).showToast();
};

const log = (message, style = "color:#008b00;font-weight:bold", ...args) => {
  if (!DEBUG) return;
  if (typeof message === "string") {
    console.log(`%c${message}`, style, ...args);
  } else {
    console.log(message, ...args);
  }
};

// ─── Overlay loader ────────────────────────────────────────────────────────────

const showWishlistOverlayLoader = () => {
  if (document.getElementById("wishlist-overlay-loader")) return;
  const overlay = document.createElement("div");
  overlay.id = "wishlist-overlay-loader";
  overlay.innerHTML = `
    <div class="wishlist-loader-content">
      <div class="spinner"></div>
      <p>Adding items to cart…</p>
    </div>
  `;
  document.body.appendChild(overlay);
};

const removeWishlistOverlayLoader = () => {
  document.getElementById("wishlist-overlay-loader")?.remove();
};

// ─── Wishlist count bubble ─────────────────────────────────────────────────────

const initWishlistsCount = (count) => {
  const el = document.querySelector(".wishlist-count-bubble");
  if (el) el.innerHTML = count;
};

const updateFavoritesCount = (status) => {
  const el = document.querySelector(".wishlist-count-bubble");
  if (!el) return;
  const current = parseInt(el.innerHTML) || 0;
  el.innerHTML = status === "added" ? current + 1 : Math.max(0, current - 1);
};

// ─── Empty-state helper ────────────────────────────────────────────────────────

const showEmptyStateIfNeeded = () => {
  const wishlistPage = document.querySelector(".wishlist-page");
  const remaining = document.querySelectorAll(".wishlist-item");
  if (remaining.length === 0 && wishlistPage) {
    wishlistPage.innerHTML = '<div class="ib-wishlist-no-items">No Items Available!</div>';
  }
};

// ─── API calls ─────────────────────────────────────────────────────────────────

/**
 * Toggle a product in/out of the wishlist.
 * `item` must be an element carrying all data-* attributes.
 * Returns the API result object, or null on failure.
 */
const toggleWishlist = async (url, customer_id, item) => {
  const product = {
    id:        item.getAttribute("data-product-id"),
    variantId: item.getAttribute("data-variant-id"),
    title:     item.getAttribute("data-product-title"),
    price:     item.getAttribute("data-product-price"),
    image:     item.getAttribute("data-product-image"),
    url:       item.getAttribute("data-product-url"),
  };

  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: BH_APP_KEY,
      },
      body: JSON.stringify({ customer_id, product }),
    });
    const result = await res.json();
    log("toggleWishlist result:", undefined, result);

    if (result.success) {
      const heartIcon = item.querySelector("i");
      if (result.added) {
        updateFavoritesCount("added");
        heartIcon?.classList.replace("fa-heart-o", "fa-heart");
      } else {
        updateFavoritesCount("removed");
        heartIcon?.classList.replace("fa-heart", "fa-heart-o");
      }
      showToast(`<strong>${product.title}</strong> ${result.message}`);
    }
    return result;
  } catch (err) {
    log("toggleWishlist error:", undefined, err);
    return null;
  }
};

const getWishlistData = async (url, customer_id) => {
  try {
    const res = await fetch(`${url}?customerId=${customer_id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: BH_APP_KEY,
      },
    });
    const result = await res.json();
    return result.success ? result.data : [];
  } catch (err) {
    log("getWishlistData error:", undefined, err);
    return [];
  }
};

const checkWishlistStatus = async (url, customer_id, product_id) => {
  try {
    const res = await fetch(`${url}/${customer_id}/${product_id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: BH_APP_KEY,
      },
    });
    const result = await res.json();
    return result.success ? result.data.isWishlisted : false;
  } catch {
    return false;
  }
};

const addToCart = async (variantId, quantity = 1) => {
  const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: variantId, quantity }),
  });
  if (!res.ok) throw new Error("Add to cart failed");
  const json = await res.json();
  window.SLIDECART_UPDATE?.();
  return json;
};

// ─── Page-level wishlist functions ────────────────────────────────────────────

const initIfUserLoggedIn = async () => {
  CUSTOMER_ID = window.cffCustomer.id;
  log(`User logged in: ${CUSTOMER_ID}`);
  const data = await getWishlistData(GET_WISHLIST_DATA_URL, CUSTOMER_ID);
  initWishlistsCount(data.length);
};

/**
 * Attach heart-icon toggle listeners on product listing/collection pages.
 * Safe to call multiple times (guarded by data-listener-attached).
 */
const addProductToWishlist = () => {
  document.querySelectorAll(".ib-favorite-heart").forEach(async (item) => {
    const productId = item.getAttribute("data-product-id");
    const isWishlisted = await checkWishlistStatus(WISHLIST_STATUS_URL, CUSTOMER_ID, productId);
    const heartIcon = item.querySelector("i");

    if (heartIcon) {
      heartIcon.classList.remove("fa-heart", "fa-heart-o", "fa-spinner", "fa-spin");
      heartIcon.classList.add(isWishlisted ? "fa-heart" : "fa-heart-o");
    }

    if (!item.dataset.listenerAttached) {
      item.addEventListener("click", async () => {
        const icon = item.querySelector("i");
        icon?.classList.add("fa-spinner", "fa-spin");
        await toggleWishlist(TOGGLE_WISHLIST_DATA_URL, CUSTOMER_ID, item);
        icon?.classList.remove("fa-spinner", "fa-spin");
      });
      item.dataset.listenerAttached = "true";
    }
  });
};

/**
 * Remove button on the wishlist page.
 * Since each row now has exactly ONE remove button, no dedup needed.
 */
const removeIndividualWishlistItem = () => {
  document.querySelectorAll(".remove-wishlist-item").forEach((button) => {
    button.addEventListener("click", async () => {
      button.disabled = true;

      const row = button.closest(".wishlist-item");
      const result = await toggleWishlist(TOGGLE_WISHLIST_DATA_URL, CUSTOMER_ID, button);

      if (result?.success) {
        log("Item removed from wishlist");
        row?.remove();
        showEmptyStateIfNeeded();
      } else {
        button.disabled = false;
      }
    });
  });
};

/**
 * Individual "Add to Cart" button on the wishlist page.
 * Since each row now has exactly ONE such button, no dedup needed.
 */
const addIndividualWishlistItemToCart = () => {
  document.querySelectorAll(".move-wishlist-to-cart").forEach((button) => {
    button.addEventListener("click", async () => {
      const productTitle = button.getAttribute("data-product-title");
      const variantId    = button.getAttribute("data-variant-id");
      const row          = button.closest(".wishlist-item");

      button.disabled    = true;
      button.textContent = "Adding…";

      try {
        await addToCart(variantId);
        log(`"${productTitle}" added to cart`);

        const result = await toggleWishlist(TOGGLE_WISHLIST_DATA_URL, CUSTOMER_ID, button);
        if (result?.success) {
          row?.remove();
          showToast(`<strong>${productTitle}</strong> has been added to your cart.`);
          showEmptyStateIfNeeded();
        }
      } catch (err) {
        log(`Error adding "${productTitle}" to cart:`, undefined, err);
        button.disabled    = false;
        button.textContent = "Add to Cart";
      }
    });
  });
};

/**
 * "Add all items to cart" button.
 *
 * FIX: Because the Liquid template now renders exactly ONE .move-wishlist-to-cart
 * per wishlist row (no mobile duplicate), querySelectorAll gives the correct count
 * and each product is toggled exactly once.
 *
 * The Set-based dedup below is kept as a belt-and-suspenders safety net in case
 * the template ever changes in future.
 */
const addAllWishlistItemsToCart = () => {
  const moveAllButton = document.getElementById("move-all-to-cart");
  if (!moveAllButton) {
    log("Move All to Cart button not found");
    return;
  }
  if (moveAllButton.dataset.listenerAttached === "true") return;
  moveAllButton.dataset.listenerAttached = "true";

  moveAllButton.addEventListener("click", async () => {
    moveAllButton.disabled    = true;
    moveAllButton.textContent = "Moving…";
    showWishlistOverlayLoader();

    // Deduplicate by product ID — safety net against any future template changes
    const seenIds     = new Set();
    const uniqueButtons = [...document.querySelectorAll(".move-wishlist-to-cart")]
      .filter((btn) => {
        const pid = btn.getAttribute("data-product-id");
        if (seenIds.has(pid)) return false;
        seenIds.add(pid);
        return true;
      });

    log(`Processing ${uniqueButtons.length} unique wishlist item(s)`);

    for (const button of uniqueButtons) {
      if (button.dataset.processing === "true") continue;
      button.dataset.processing = "true";

      const variantId = button.getAttribute("data-variant-id");
      const row       = button.closest(".wishlist-item");

      try {
        await addToCart(variantId);

        const result = await toggleWishlist(TOGGLE_WISHLIST_DATA_URL, CUSTOMER_ID, button);
        log("Toggle result:", undefined, result);

        // Only remove the row when the API confirms item was removed (added === false)
        if (result?.success && result.added === false) {
          row?.remove();
        } else {
          log("Unexpected toggle state — row kept:", undefined, result);
          button.dataset.processing = "false";
        }
      } catch (err) {
        log("Error processing item:", undefined, err);
        button.textContent        = "Failed";
        button.disabled           = false;
        button.dataset.processing = "false";
      }
    }

    removeWishlistOverlayLoader();
    showEmptyStateIfNeeded();

    moveAllButton.disabled    = false;
    moveAllButton.textContent = "Add all items to cart";
  });
};

// ─── Header wishlist icon redirect ────────────────────────────────────────────

const redirectToWishlistPage = () => {
  document.getElementById("wishlist")?.addEventListener("click", () => {
    window.location.href = "/pages/favorites";
  });
};

const init = () => {
  log("Initializing wishlist script…");
  redirectToWishlistPage();
  log("Wishlist script initialized");
};

// ─── DOM mutation observer (re-runs addProductToWishlist after AJAX updates) ──

const observeDOMChanges = (targetNode = document.body, delay = 300) => {
  let timeout = null;
  const observer = new MutationObserver(() => {
    clearTimeout(timeout);
    timeout = setTimeout(() => addProductToWishlist(), delay);
  });
  observer.observe(targetNode, { childList: true, subtree: true });
  return observer;
};

// ─── Boot ──────────────────────────────────────────────────────────────────────

document.addEventListener("DOMContentLoaded", async () => {
  init();
  log("DOMContentLoaded — wishlist script ready");

  if (window.cffCustomer) {
    await initIfUserLoggedIn();
    addProductToWishlist();
    removeIndividualWishlistItem();
    addIndividualWishlistItemToCart();
    addAllWishlistItemsToCart();
    observeDOMChanges();
  } else {
    // Guest user — show login popup when heart is clicked
    // Capture phase (true) ensures this fires before any other listener
    document.addEventListener(
      "click",
      (e) => {
        const heart = e.target.closest(".ib-favorite-heart");
        if (!heart) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        document.querySelector(".ib-login-alert.popup")?.style.setProperty("display", "flex");
      },
      true
    );
  }
});

// Fallback for dynamically-injected hearts after initial page load (e.g. colour swatches)
document.body.addEventListener("click", (e) => {
  const heart = e.target.closest(".ib-favorite-heart");
  if (heart && !window.cffCustomer) {
    document.querySelector(".ib-login-alert.popup")?.style.setProperty("display", "flex");
    e.preventDefault();
  }
});